import { track } from '@eventbrite/datalayer-library';
import { Icon } from '@eventbrite/eds-icon';
import { ChevronDownChunky, MapPinChunky } from '@eventbrite/eds-iconography';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { Badge, Button, CloseIcon, Typography } from '@eventbrite/marmalade';
import React, { useEffect, useState } from 'react';
import { LocationAutocompleteContainer } from '../../../../containers/LocationAutocompleteContainer';
import stars from '../../../../static/images/browse/chatbot_stars.svg';
import styles from './ChatBot.module.scss';
const trackbotEvents = (event: any) => {
    track({
        eventName: 'chatbot-events',
        eventData: {
            eventName: event.eventName,
            question: event.question || null,
        },
    });
};

const ChevronIcon = (
    <Icon color="white" size="small" type={<ChevronDownChunky />} />
);
const LocationPin = <Icon type={<MapPinChunky />} color="white" size="small" />;
interface ChatBotProps {
    currentPlace: string;
    isBotRequest: boolean;
    handleUseCurrentLocation?: Function;
    handleLocationSelect?: Function;
    history?: History;
    h1Prefix?: string;
}

export const ChatBot = ({
    currentPlace,
    isBotRequest,
    handleUseCurrentLocation,
    handleLocationSelect,
    history,
    h1Prefix,
}: ChatBotProps) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [loadScript, setloadScript] = useState(false);

    useEffect(() => {
        trackbotEvents({ eventName: 'CHATBOT_BANNER_LOADED' });
    }, []);

    useEffect(() => {
        if (modalIsOpen && !loadScript) {
            // load the third-party script when the modal is open first time
            const script = document.createElement('script');
            script.src =
                'https://decagon.ai/loaders/eventbrite_discovery_embed.js';
            script.async = true;
            if (script) {
                script.addEventListener('load', () => {
                    if (HAS_WINDOW && window.decagonEmbed) {
                        window.decagonEmbed.setEventListener(trackbotEvents);
                        window.decagonEmbed.setMetadata({
                            placeId: currentPlace,
                        });
                    }
                });
            }
            document.body.appendChild(script);
            setloadScript(true);
        }
    }, [modalIsOpen, loadScript, currentPlace]);

    const openModal = () => {
        trackbotEvents({ eventName: 'OPEN_CHATBOT' });
        setModalIsOpen(true);
    };

    const closeModal = () => {
        trackbotEvents({ eventName: 'CLOSE_CHATBOT' });
        setModalIsOpen(false);
    };

    const locationPicker = (
        <span
            className={styles.cityBrowseHeaderLocationAutocomplete}
            data-current-text={currentPlace}
            data-testid="city-browse-header"
            data-spec="location-autocomplete"
            style={
                {
                    '--character-count': currentPlace?.length || 10,
                } as any
            }
        >
            {isBotRequest ? (
                currentPlace
            ) : (
                <LocationAutocompleteContainer
                    handleUseCurrentLocation={handleUseCurrentLocation}
                    handleLocationSelect={handleLocationSelect}
                    suffix={ChevronIcon}
                    prefix={LocationPin}
                    history={history}
                    autocompleteProps={{
                        borderType: 'content',
                        preventInputValuePrefill: true,
                        id: 'browse-location-selector',
                    }}
                />
            )}
        </span>
    );
    return (
        <div className={styles.root}>
            <div className={styles.parentContainer}>
                <div className={styles.textContainer}>
                    <Badge variant="bold">{gettext('BETA')}</Badge>

                    <Typography
                        variant="heading-lg"
                        color="neutral-800"
                        className={styles.smallFont}
                    >
                        {gettext('A new interactive way to find things to do')}
                    </Typography>
                    <Typography variant="body-lg" color="neutral-600">
                        {gettext('Make plans tailored to your interests')}
                    </Typography>
                    <Button
                        className={styles.chatbutton}
                        variant="primary"
                        onClick={openModal}
                        data-heap-id="city-browse-chat-with-us-button"
                    >
                        {gettext('Chat with us')}
                        <img src={stars} alt="Stars Image"></img>
                    </Button>
                </div>
                <div className={styles.rightContainer}>
                    <div className={styles.rightBackground1}></div>
                    <div className={styles.rightBackground2}></div>
                </div>
            </div>
            <div className={styles.locationContainer}>
                {locationPicker}
                <h1 className={styles.hdiv}>
                    <Typography>
                        {h1Prefix ? `${h1Prefix} ` : gettext('Best Events in ')}
                    </Typography>
                    <Typography>{currentPlace}</Typography>
                </h1>
            </div>
            <div
                className={`${styles.modalOverlay} ${
                    modalIsOpen ? styles.showModal : styles.hideModal
                }`}
            >
                <div className={styles.modal}>
                    <CloseIcon
                        onClick={closeModal}
                        className={styles.closeModal}
                    />
                    <div
                        id="decagon-embed-container"
                        className={styles.decagonContainer}
                    ></div>
                </div>
            </div>
        </div>
    );
};
